import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
    id: number;
    message: string;
    type: ToastType;
}

interface ToastNotificationProps {
    toasts: Toast[];
    onClose: (id: number) => void;
    duration?: number;
}

const getIcon = (type: ToastType) => {
    switch (type) {
        case 'success':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                    <path d="M5 12l5 5l10 -10"></path>
                </svg>
            );
        case 'error':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                    <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                    <path d="M12 8v4"></path>
                    <path d="M12 16h.01"></path>
                </svg>
            );
        case 'warning':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                    <path d="M12 9v4"></path>
                    <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                    <path d="M12 16h.01"></path>
                </svg>
            );
        case 'info':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                    <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                    <path d="M12 9h.01"></path>
                    <path d="M11 12h1v4h1"></path>
                </svg>
            );
    }
};

const getAlertClass = (type: ToastType) => {
    switch (type) {
        case 'success':
            return 'alert-success';
        case 'error':
            return 'alert-danger';
        case 'warning':
            return 'alert-warning';
        case 'info':
            return 'alert-info';
    }
};

interface ToastItemProps {
    toast: Toast;
    onClose: (id: number) => void;
    duration: number;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose, duration }) => {
    // Error toasts carry information the user has to read (what failed and
    // why), so they never auto-dismiss — they stay until explicitly closed.
    const sticky = toast.type === 'error';

    useEffect(() => {
        if (sticky) return;
        const timer = setTimeout(() => onClose(toast.id), duration);
        return () => clearTimeout(timer);
    }, [toast.id, duration, onClose, sticky]);

    return (
        <div className={`alert alert-important ${getAlertClass(toast.type)}`} role="alert">
            <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex">
                    <div className="alert-icon me-2">
                        {getIcon(toast.type)}
                    </div>
                    {/* Messages arrive with their own line breaks - a workflow's
                        breakdown, a list of field errors joined by newline - and HTML
                        would otherwise run them together into one paragraph. */}
                    <div className="flex-grow-1" style={{whiteSpace: 'pre-line'}}>
                        {toast.message}
                    </div>
                </div>
                {sticky ? (
                    <button type="button" className="btn btn-sm btn-light ms-3 flex-shrink-0" onClick={() => onClose(toast.id)}>
                        Zavrieť
                    </button>
                ) : (
                    <button type="button" className="btn-close" onClick={() => onClose(toast.id)} aria-label="Close"></button>
                )}
            </div>
        </div>
    );
};

export const ToastNotification: React.FC<ToastNotificationProps> = ({
    toasts,
    onClose,
    duration = 3000
}) => {
    if (toasts.length === 0) return null;

    return (
        <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999, minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {toasts.map(toast => (
                <ToastItem key={toast.id} toast={toast} onClose={onClose} duration={duration} />
            ))}
        </div>
    );
};